import { util as sdkUtil } from './sdkLoader';

/**
 * Centralized image-variant config for Sharetribe SDK queries.
 *
 * Pick a set (or several) per use case and spread the result into your
 * sdk.listings.query() / sdk.users.show() params:
 *
 *   sdk.listings.query({
 *     ...listingCardIncludeParams(),
 *     pub_type: 'page',
 *   });
 *
 * To add a new variant: register its dimensions in CUSTOM_VARIANTS
 * (only for custom names — built-in names like landscape-crop don't need it),
 * then add the name to whatever set(s) need it.
 */

// Helper mirroring the web-template's createImageVariantConfig
export const createImageVariantConfig = (name, w, h, fit = 'crop') => ({
  [`imageVariant.${name}`]: sdkUtil.objectQueryString({ w, h, fit }),
});

// Dimensions for our custom variants. Built-in Sharetribe variants
// (landscape-crop*, scaled-*, square-small/medium, default, facebook, twitter)
// are predefined by the platform and don't need imageVariant.* params.
const CUSTOM_VARIANTS = {
  'portrait-crop': { w: 280, h: 392, fit: 'crop' },
  'portrait-crop2x': { w: 560, h: 784, fit: 'crop' },
  'portrait-crop4x': { w: 560, h: 784, fit: 'crop' },
  'square-xsmall': { w: 40, h: 40, fit: 'crop' },
  'square-xsmall2x': { w: 80, h: 80, fit: 'crop' },
  'hero-scale': { w: 1440, h: 400, fit: 'scale' },
  'hero-crop': { w: 1440, h: 400, fit: 'crop' },
};

// Named variant sets per use case. Every variant currently referenced in
// src/ appears in at least one set:
//   default, facebook, twitter
//   hero-crop, hero-scale
//   landscape-crop, landscape-crop2x, landscape-crop4x, landscape-crop6x
//   portrait-crop, portrait-crop2x, portrait-crop4x
//   scaled-small, scaled-medium, scaled-large, scaled-xlarge
//   square-small, square-small2x, square-medium, square-xsmall, square-xsmall2x
const SETS = {
  // Listing card grids: LandingPage, ListingPage related lists, search, etc.
  listingCard: [
    'landscape-crop',
    'landscape-crop2x',
    'landscape-crop4x',
    'landscape-crop6x',
    'portrait-crop',
    'portrait-crop2x',
    'portrait-crop4x',
    'scaled-small',
    'scaled-medium',
    'scaled-large',
  ],

  // Listing detail page: hero + image carousel + social meta tags
  listingDetail: [
    'landscape-crop',
    'landscape-crop2x',
    'landscape-crop4x',
    'landscape-crop6x',
    'portrait-crop',
    'portrait-crop2x',
    'portrait-crop4x',
    'hero-scale',
    'hero-crop',
    'scaled-small',
    'scaled-medium',
    'scaled-large',
    'scaled-xlarge',
    'facebook',
    'twitter',
  ],

  // Stream page host/listing card (small thumbs)
  streamCard: ['square-small', 'landscape-crop', 'landscape-crop2x'],

  // Search results (minimal landscape thumbs)
  searchResults: ['landscape-crop', 'landscape-crop2x'],

  // Avatars — covers every avatar size used across the app
  avatar: [
    'square-xsmall',
    'square-xsmall2x',
    'square-small',
    'square-small2x',
    'square-medium',
    'default',
  ],

  // Manage / subscription pages (slightly larger avatar)
  manageAvatar: ['square-small', 'square-medium'],

  // Edit Show flow — portrait previews on the show wizard / image upload
  editShow: ['portrait-crop', 'portrait-crop2x', 'portrait-crop4x'],

  // Edit Live Room flow — landscape previews on the live room wizard / image upload
  editLiveRoom: [
    'landscape-crop',
    'landscape-crop2x',
    'landscape-crop4x',
    'landscape-crop6x',
  ],

  // Community: post avatars + featured post imagery
  community: ['square-small', 'square-small2x', 'default', 'portrait-crop2x'],
};

/**
 * Build SDK params for one or more named sets.
 * Returns: { 'fields.image': [...], 'imageVariant.foo': '...' }
 *
 * Combining sets de-duplicates variants automatically.
 */
export const imageVariantsForSets = (...setNames) => {
  const all = setNames.flatMap(name => {
    const set = SETS[name];
    if (!set && process.env.NODE_ENV !== 'production') {
      console.warn(`[imageVariants] Unknown set "${name}"`);
    }
    return set || [];
  });
  const unique = [...new Set(all)];
  const customParams = unique.reduce((acc, name) => {
    if (!CUSTOM_VARIANTS[name]) return acc;
    const { w, h, fit } = CUSTOM_VARIANTS[name];
    return { ...acc, ...createImageVariantConfig(name, w, h, fit) };
  }, {});
  return {
    'fields.image': unique.map(n => `variants.${n}`),
    ...customParams,
  };
};

// Standard listing fields requested across the app
export const LISTING_FIELDS = [
  'title',
  'description',
  'geolocation',
  'price',
  'state',
  'publicData',
  'metadata',
];

/**
 * Listing detail (full info + hero + social) — for ListingPage / LiveRoomPage
 * showListing / similar related queries.
 */
export const listingDetailIncludeParams = () => ({
  include: ['author', 'author.profileImage', 'images'],
  'fields.listing': LISTING_FIELDS,
  ...imageVariantsForSets('listingDetail', 'avatar'),
});

/**
 * Listing card grids — LandingPage, search, related lists.
 */
export const listingCardIncludeParams = () => ({
  include: ['author', 'author.profileImage', 'images'],
  'fields.listing': LISTING_FIELDS,
  ...imageVariantsForSets('listingCard', 'avatar'),
});

/**
 * Avatar-only fetch (e.g. user profile thumbnail in chat / mentions / cards).
 */
export const avatarIncludeParams = () => ({
  include: ['profileImage'],
  ...imageVariantsForSets('avatar'),
});

// ====================================================================
// Edit-flow helpers — used by EditListingPage / EditLiveRoomPage ducks
// ====================================================================
//
// Each edit page has two distinct call sites:
//   • Listing fetch / draft create / update — needs `include` so the
//     response brings author + images back. Used by loadData,
//     requestCreateListingDraft, requestUpdateListing.
//   • Image upload — sdk.images.upload only returns the new image, so
//     `include` doesn't apply; we just want the variant params on the
//     response so the UI can preview the upload at the right size.
//
// Show flows use portrait-* variants; LiveRoom flows use landscape-*.

const editIncludeShape = setName => ({
  expand: true,
  include: ['author', 'images'],
  ...imageVariantsForSets(setName),
});

const editImageUploadShape = setName => ({
  expand: true,
  ...imageVariantsForSets(setName),
});

// Show wizard
export const editShowIncludeParams = () => editIncludeShape('editShow');
export const editShowImageUploadParams = () => editImageUploadShape('editShow');

// Live Room wizard
export const editLiveRoomIncludeParams = () => editIncludeShape('editLiveRoom');
export const editLiveRoomImageUploadParams = () => editImageUploadShape('editLiveRoom');
